<script setup>
import { ref } from 'vue'
import ComplexItem from '@/components/menu/items/ComplexItem.vue'
import MealItem from '@/components/menu/items/MealItem.vue'
import SimpleItem from '@/components/menu/items/SimpleItem.vue'
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue'
import { ChevronUpIcon } from '@heroicons/vue/20/solid'
import { useRestoGLobalStore } from '@/stores/restoGlobalStore'
import { useCartGLobalStore } from '@/stores/cartGlobalStore'
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import placeholderBg from '@/assets/images/placeholder_img.png'

const restoStore = useRestoGLobalStore()
const cartStore = useCartGLobalStore()
const itemData = ref(null)
const is_mobile = ref(false)
const handleItemModal = (product) => {
  if (cartStore.CART_DATA?.order_type == 'delivery' && !cartStore?.CART_DATA?.pc_data?.is_valid) {
    cartStore.SHOW_POSTCODE_MODAL = true
  } else {
    itemData.value = product
    restoStore.OPEN_MODAL = true
  }
}
</script>
<template>
  <div class="px-1 py-5 md:mb-0 mb-24 transition-colors duration-300">
    <Disclosure
      v-for="category in restoStore.MENU_DATA?.categories"
      :id="`category-div-id-${category.category_id}`"
      :key="category.category_id"
      v-show="category.category_name !== 'Gift Voucher'"
      v-slot="{ open }"
      as="div"
      :default-open="!is_mobile"
      class="mb-5 last:mb-0 rounded-lg shadow-lg dark:shadow-gray-900/20 bg-white dark:bg-surface-dark overflow-hidden transition-all duration-300 hover:shadow-xl"
    >
      <template v-if="category.category_name !== 'Gift Voucher'">
      <DisclosureButton
        :id="`category-id-${category.category_id}`"
        class="flex w-full justify-between md:px-5 sm:px-4 px-3 py-4 text-left text-sm font-medium text-white hover:text-white focus:outline-none focus-visible:ring focus-visible:ring-indigo-500 dark:focus-visible:ring-indigo-400 focus-visible:ring-opacity-75 transition-all duration-300 ease-in-out backdrop-blur-sm bg-black/10"
        :style="{
          background:
            'linear-gradient(0deg, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.3)), url(' +
            placeholderBg +
            ')',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'repeat'
        }"
      >
        <h2
          class="text-lg lg:text-xl font-bold tracking-tight text-white sm:text-1xl transition-colors duration-200 drop-shadow-md"
        >
          {{ category.category_name }}
        </h2>

        <ChevronUpIcon
          :class="open ? 'rotate-180 transform text-white' : 'text-white/80'"
          class="h-8 w-8 transition-transform duration-300 ease-in-out"
        />
        <div class="bg-skew-right"></div>
      </DisclosureButton>
      <DisclosurePanel
        class="md:px-5 px-4 pt-4 md:pb-6 sm:pb-5 pb-4 text-sm text-gray-500 dark:text-gray-400 transition-all duration-300 ease-in-out bg-white dark:bg-surface-dark"
      >
        <p
          v-if="category.info"
          class="pb-4 text-md italic font-medium text-gray-600 dark:text-gray-300 border-b border-gray-100 dark:border-gray-700 mb-4"
        >
          {{ category.info }}
        </p>
        <div class="grid lg:grid-cols-2 grid-cols-1 gap-5">
          <template v-for="product in category.products" :key="product.product_id">
            <template v-if="product.items.length == 1">
              <ComplexItem
                v-if="product.items[0].is_complex_item"
                :complex-item-data="product.items[0]"
                :product-info="product.info"
                :item-count="product.items.length"
              ></ComplexItem>
              <MealItem
                v-else-if="product.items[0].is_meal"
                :meal-item-data="product.items[0]"
                :product-info="product.info"
                :item-count="product.items.length"
              ></MealItem>
              <SimpleItem
                v-else
                :simple-item-data="product.items[0]"
                :product-info="product.info"
                :item-count="product.items.length"
              ></SimpleItem>
            </template>
            <div
              v-else
              class="flex items-center rounded-lg border border-gray-200 dark:border-gray-700 p-4 cursor-pointer hover:border-indigo-400 hover:shadow-lg dark:hover:border-indigo-500 bg-white dark:bg-surface-dark transition-all duration-200 transform hover:scale-[1.02]"
              @click="handleItemModal(product)"
            >
              <div class="min-w-0 flex-1">
                <h3
                  as="h3"
                  class="text-base font-semibold leading-6 text-gray-900 dark:text-text-dark"
                >
                  {{ product.product_name }}
                  <span v-if="product.items.length > 1" class="text-sm font-semibold text-indigo-600 dark:text-indigo-400">
                    ({{ product.items.slice(0, 3).map(item => item.item_syn).join(', ') }}{{ product.items.length > 3 ? '...' : '' }})
                  </span>
                </h3>
                <p
                  v-if="product.info"
                  class="line-clamp-2 text-sm text-gray-500 dark:text-gray-400 mb-0 mt-1"
                >
                  {{ product.info }}
                </p>
              </div>
              <div v-if="product.menu_img_url" class="flex-shrink-0 ml-3">
                <div
                  class="h-16 w-16 rounded-md overflow-hidden shadow-md"
                  :style="{
                    backgroundImage: 'url(' + product?.menu_img_url + ')',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }"
                ></div>
              </div>
            </div>
          </template>
        </div>
      </DisclosurePanel>
      </template>
    </Disclosure>
  </div>
  <TransitionRoot as="template" :show="restoStore.OPEN_MODAL">
    <Dialog as="div" class="relative z-20" @close="restoStore.hideModal()">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div
          class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity backdrop-blur-sm"
        ></div>
      </TransitionChild>

      <div class="fixed inset-0 z-20 overflow-y-auto">
        <div class="flex min-h-full justify-center p-4 text-center items-center sm:p-0">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              class="relative transform rounded-lg bg-white dark:bg-surface-dark text-left shadow-xl transition-all my-8 w-full max-w-md overflow-hidden"
            >
              <div class="absolute top-0 right-0 pt-2 pr-2 z-10 block">
                <button
                  type="button"
                  class="rounded-full border border-e-violet p-1 bg-white dark:bg-gray-700 text-e-violet hover:text-white hover:bg-e-violet dark:hover:bg-indigo-600 focus:outline-none shadow-md transition-colors duration-200"
                  @click="restoStore.hideModal()"
                >
                  <span class="sr-only">Close</span>
                  <XMarkIcon class="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
              <div
                class="max-h-90vh overflow-auto bg-white dark:bg-surface-dark transition-colors duration-200"
              >
                <div
                  v-if="itemData?.menu_img_url"
                  class="mx-auto mb-3 h-64 bg-gray-200 dark:bg-gray-700 relative"
                  :style="{
                    backgroundImage: 'url(' + itemData?.menu_img_url + ')',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }"
                >
                  <div class="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                  <h3 class="absolute bottom-0 left-0 right-0 text-xl font-bold text-white p-4">
                    {{ itemData?.product_name }}
                  </h3>
                </div>

                <div class="px-5 py-6 dark:text-text-dark">
                  <div v-if="!itemData?.menu_img_url" class="mb-4 text-center">
                    <h3 class="text-xl font-bold text-gray-900 dark:text-text-dark">
                      {{ itemData?.product_name }}
                    </h3>
                    <p v-if="itemData?.info" class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {{ itemData?.info }}
                    </p>
                  </div>
                  <p
                    v-else-if="itemData?.info"
                    class="text-sm text-gray-600 dark:text-gray-300 mb-4"
                  >
                    {{ itemData?.info }}
                  </p>

                  <div
                    v-for="item in itemData?.items"
                    :key="item.item_id"
                    class="mt-3 mb-3 transition-colors duration-200"
                  >
                    <ComplexItem
                      v-if="item.is_complex_item"
                      :complex-item-data="item"
                    ></ComplexItem>
                    <MealItem v-else-if="item.is_meal" :meal-item-data="item"></MealItem>
                    <SimpleItem v-else :simple-item-data="item"></SimpleItem>
                  </div>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<style scoped>
.bg-skew-right {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  width: 50%;
  height: 100%;
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.4));
  transform: skewX(-45deg);
  transform-origin: top right;
  z-index: -1;
}
.dark .bg-skew-right {
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.4));
}
</style>
